import type { PhoneRevealSummaryRow, StaffVisibilityView } from "@planerius/contracts/cabinet"

import { plural } from "../../lib/plural"

/**
 * 4-Б12 «Что мастер видит о клиенте» как данные, отдельно от разметки.
 *
 * ДВЕ НАСТРОЙКИ, И ОБЕ ПРО ДОВЕРИЕ, А НЕ ПРО ИНТЕРФЕЙС. Право отвечает «что положено
 * по должности», настройка — «насколько салон доверяет коллективу» (ТЗ 04, Б12).
 * Поэтому у каждой подписано последствие, а у включения и выключения — РАЗНЫЕ
 * подтверждения: включить значит открыть базу шире, выключить — отобрать то, чем
 * мастер уже пользуется, и оба решения человек принимает, прочитав.
 */

export interface VisibilityToggle {
  readonly field: "showClientPhoneToMasters" | "showMoneyToMasters"
  readonly title: string
  readonly on: string
  readonly off: string
  /** Что спросить перед включением. */
  readonly confirmOn: string
  /** Что спросить перед выключением. */
  readonly confirmOff: string
}

export const VISIBILITY_TOGGLES: readonly VisibilityToggle[] = [
  {
    field: "showClientPhoneToMasters",
    title: "Мастер видит телефон своего клиента",
    on: "Номер открывается сразу в карточке визита. Каждое открытие остаётся в журнале салона.",
    off: "Номер закрыт маской, мастер открывает его нажатием «Показать». Открытия тоже остаются в журнале.",
    confirmOn:
      "Мастера увидят номера своих клиентов сразу при открытии визита. Журнал открытий продолжит вестись, но предупреждения «сейчас откроется номер» у мастера больше не будет. Включить?",
    confirmOff:
      "Мастера перестанут видеть номер сразу — останется маска и кнопка «Показать». Тем, кто звонит клиентам сам, добавится одно нажатие на каждый визит. Выключить?",
  },
  {
    field: "showMoneyToMasters",
    title: "Мастер видит деньги",
    on: "В карточке визита видна цена услуги, а в своём дне — сумма своих визитов.",
    off: "Цены и суммы мастеру не приходят вовсе: сервер не отдаёт их, а не прячет на экране.",
    confirmOn:
      "Мастера увидят цену услуги в своих визитах и сумму своей выработки за день. Чужие визиты и выручку салона это не открывает. Включить?",
    confirmOff:
      "Мастера перестанут видеть цены и свою выработку. Вопрос «сколько я сегодня заработал» будет задаваться администратору. Выключить?",
  },
]

export function toggleText(toggle: VisibilityToggle, value: boolean): string {
  return value ? toggle.on : toggle.off
}

export function confirmText(toggle: VisibilityToggle, next: boolean): string {
  return next ? toggle.confirmOn : toggle.confirmOff
}

/**
 * «11 открытий · 1 клиент» — и то и другое, потому что разговор именно о разнице.
 * Двадцать открытий одного клиента за день — работа, двадцать разных подряд — перебор
 * базы глазами.
 */
export function revealLine(row: PhoneRevealSummaryRow): string {
  const reveals = `${String(row.reveals)} ${plural(row.reveals, "открытие", "открытия", "открытий")}`
  const clients = `${String(row.clients)} ${plural(row.clients, "клиент", "клиента", "клиентов")}`
  return `${reveals} · ${clients}`
}

/** «7 дней», «31 день» — глубина сводки склоняется, как всё остальное в кабинете. */
export function daysLabel(days: number): string {
  return `${String(days)} ${plural(days, "день", "дня", "дней")}`
}

/**
 * Пустой журнал — это ответ, а не отсутствие данных.
 *
 * Глубина подставляется: сводка может прийти и за месяц, а слово «неделя» в тексте
 * тогда врёт. Утверждение «никто не открывал» произносится ТОЛЬКО на настоящем пустом
 * ответе — не на отказе и не на ожидании (см. revealsFailureText).
 */
export function revealEmptyText(days: number): string {
  return `За ${daysLabel(days)} номера никто не открывал. Строка появится при первом открытии — журнал ведётся в обоих положениях настройки.`
}

/**
 * Журнал не загрузился — это НЕ «открытий не было».
 *
 * Единственный экран, ради которого журнал заведён, в сломанном состоянии успокаивал
 * владельца положительным утверждением о безопасности. Отказ обязан выглядеть отказом.
 */
export const REVEAL_FAILED =
  "Журнал открытий не загрузился. Это не значит, что открытий не было, — повторите попытку."

/**
 * Что показать, когда сохранение не прошло.
 *
 * КОНФЛИКТ ВЕРСИЙ — ОТДЕЛЬНЫЙ СЛУЧАЙ. «Прежнее значение продолжает действовать» для
 * него ложь по определению: 409 приходит ровно потому, что настройку только что изменил
 * кто-то другой, возможно на противоположную. Экран тут же показывает свежий
 * переключатель, и рядом с ним фраза «мастера видят то же, что видели минуту назад»
 * отправляла владельца домой с уверенностью, что номера закрыты.
 */
export function saveFailureText(detail: string | null, code?: string | null): string {
  const reason = detail === null || detail === "" ? "" : ` ${detail}`
  if (code === "version_conflict") {
    return `Настройка не сохранена.${reason} Её изменил кто-то другой: сейчас действует то, что показано на экране. Проверьте значение и повторите, если оно не то.`
  }
  return `Настройка не сохранена.${reason} Прежнее значение продолжает действовать — мастера видят то же, что видели минуту назад.`
}

export function isChanged(before: StaffVisibilityView, after: StaffVisibilityView): boolean {
  return (
    before.showClientPhoneToMasters !== after.showClientPhoneToMasters ||
    before.showMoneyToMasters !== after.showMoneyToMasters
  )
}
